// Row-to-chart-data mapping for the visualization cards (re-exported by charts.js).
import { displayLabel, schemaLabel } from './labels';

// Aggregate one field over rows ("count" ignores the field); exported for the map donuts.
export function aggregateValues(rows, field, agg) {
  if (agg === 'count') return rows.length;
  const values = rows.map((r) => Number(r[field])).filter((v) => Number.isFinite(v));
  if (values.length === 0) return null;
  switch (agg) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default:
      return values.reduce((a, b) => a + b, 0) / values.length;
  }
}

// Single-series bar/pie/radar data: one entry per row, in input order.
// Aggregation happens in the pipeline (group/aggregate nodes), so the chart
// simply plots each row's y value against its x label.
export function prepareCategories(rows, fields, config) {
  const x = config && config.x;
  const y = config && config.y;
  if (!x || !y) return [];

  return (rows || []).map((row) => ({
    label: groupLabelOf(row[x]),
    value: numericOf(row[y])
  }));
}

// Multi-series bar/pie/radar data: one dataset per value of seriesField, each
// plotting its own rows (x label -> y value; the last row wins per label).
// Returns { labels, series: [{ label, data }] } in first-seen order, or null
// when no series field is set.
export function prepareSeries(rows, fields, config) {
  const x = config && config.x;
  const y = config && config.y;
  const seriesField = config && config.seriesField;
  if (!x || !y || !seriesField) return null;

  const bySeries = new Map();
  const labels = [];
  for (const row of rows || []) {
    const label = groupLabelOf(row[x]);
    if (!labels.includes(label)) labels.push(label);
    const seriesLabel = groupLabelOf(row[seriesField]);
    if (!bySeries.has(seriesLabel)) bySeries.set(seriesLabel, {});
    bySeries.get(seriesLabel)[label] = numericOf(row[y]);
  }

  return {
    labels,
    series: Array.from(bySeries.entries()).map(([label, byX]) => ({
      label,
      data: labels.map((l) => (byX[l] !== undefined ? byX[l] : null))
    }))
  };
}

function groupLabelOf(raw) {
  return raw === null || raw === undefined ? '—' : String(raw);
}

// A chart value must be finite; anything else plots as a gap.
function numericOf(raw) {
  if (raw === null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// Multi-series line data: one dataset per value of seriesField over the
// x categories. Returns { labels, series: [{ label, data }] } or null when
// no series field is set.
export function prepareLineSeries(rows, fields, config) {
  const x = config && config.x;
  const y = config && config.y;
  const seriesField = config && config.seriesField;
  if (!x || !y || !seriesField) return null;

  const bySeries = new Map();
  const labels = [];
  for (const row of rows || []) {
    if (row[x] === null || row[x] === undefined) continue;
    const label = String(row[x]);
    if (!labels.includes(label)) labels.push(label);
    const seriesLabel = groupLabelOf(row[seriesField]);
    if (!bySeries.has(seriesLabel)) bySeries.set(seriesLabel, {});
    const value = Number(row[y]);
    if (Number.isFinite(value)) bySeries.get(seriesLabel)[label] = value;
  }

  return {
    labels,
    series: Array.from(bySeries.entries()).map(([label, byX]) => ({
      label,
      data: labels.map((l) => (byX[l] !== undefined ? byX[l] : null))
    }))
  };
}

// Scatter/line points: [x, y] pairs with an optional series (color field).
export function preparePoints(rows, fields, config) {
  const xKey = config && config.x;
  const yKey = config && config.y;
  const colorKey = config && config.color;
  if (!xKey || !yKey) return [];

  const points = [];
  for (const row of rows || []) {
    const rawX = row[xKey];
    const rawY = row[yKey];
    if (rawX === null || rawX === undefined || rawY === null || rawY === undefined) continue;
    const x = Number(rawX);
    const y = Number(rawY);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    points.push({
      x,
      y,
      series: colorKey ? String(row[colorKey] ?? '—') : null,
      raw: row
    });
  }
  return points;
}

// Line data: categories on the x-axis, values on the y-axis.
export function prepareLine(rows, fields, config) {
  const x = config && config.x;
  const y = config && config.y;
  if (!x || !y) return [];
  return (rows || [])
    .filter((row) => row[x] !== null && row[x] !== undefined)
    .map((row) => ({ label: String(row[x]), value: Number(row[y]) }));
}

// Stacked bar data: per-label `series` counts cross-tabulated from distribution__ columns.
// `config.labels` maps a column key to a custom legend/tooltip label; `fields`
// supplies the schema label used as the default when no override is set.
export function prepareStacked(rows, config, fields) {
  const x = config && config.x;
  const series = config && config.series;
  if (!x || !series) return { labels: [], series: [] };
  const labels = [];
  for (const row of rows) {
    labels.push(row[x]);
  }

  const data = series.map((key) => ({
    label: displayLabel(config.labels, key, schemaLabel(fields, key)),
    series: []
  }));

  // labels and rows implictly have the same order
  for (const row of rows) {
    series.map((key, idx) => {
      data[idx].series.push(row[key]);
    });
  }
  return { labels, series: data };
}

// Donut parts of one map cluster: [{ label, value }] in first-seen order.
// Without a value field each part counts members (or reads distribution__
// columns); with one it aggregates that field per part. Cross-tabulated rows
// keep the segment as distribution__ columns, so read those directly.
// `labels` optionally maps a column key to a custom segment label.
export function donutParts(rows, donutField, valueField, agg, labels) {
  const first = (rows || [])[0];
  if (!valueField || (donutField && first && first[donutField] === undefined)) {
    return clusterParts(rows, donutField, labels);
  }

  const groups = new Map();
  for (const row of rows || []) {
    const raw = row ? row[donutField] : null;
    const label = raw === null || raw === undefined ? '—' : String(raw);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(row);
  }

  return Array.from(groups.entries()).map(([label, members]) => ({
    label,
    value: aggregateValues(members, valueField, agg || 'mean')
  }));
}

export function clusterParts(rows, seriesKey, labels) {
  const row = rows[0];

  return seriesKey.map((key) => {
    return { label: displayLabel(labels, key, key), value: row[key] || 0 };
  });
}
